import ApiService from '@/data/services/ApiService'
import { IPlayer, IPlayerCreateForm, IPlayerPaged } from '@/data/models/Player'
import { IPaginationQueryParams } from '@/data/models/Pagination'
import { AxiosResponse } from 'axios'
import { IPaginatedServiceResponse, IServiceResponse } from '@/data/models/ServiceResponse'

export interface IPlayerGetPaginatedQuery extends IPaginationQueryParams {
  team_id?: number;
  with_attribute_values?: boolean;
}

export class PlayerApiService extends ApiService {
  /**
   * Returns paginated list of Players
   *
   * @param query
   */
  static async getPaginated(query: IPlayerGetPaginatedQuery): Promise<IPaginatedServiceResponse<IPlayerPaged>> {
    return this.axios.get('players', { params: query })
      .then((response: AxiosResponse<IPaginatedServiceResponse<IPlayerPaged>>) => response.data)
  }

  static async getAll(query: IPlayerGetPaginatedQuery): Promise<IServiceResponse<IPlayer>> {
    return this.axios.get('players', { params: query })
      .then((response: AxiosResponse<IServiceResponse<IPlayer>>) => response.data)
  }

  static async getSimpleList(query: IPlayerGetPaginatedQuery): Promise<IServiceResponse<IPlayer>> {
    return this.axios.get('players', { params: { ...query, no_stats: true } })
      .then((response: AxiosResponse<IServiceResponse<IPlayer>>) => response.data)
  }

  /**
   * Given player's id, fetches player data from database
   *
   * @param id
   */
  static async getPlayerDetails(id: string): Promise<IServiceResponse<IPlayer>> {
    return this.axios.get(`players/${id}`)
      .then((response: AxiosResponse) => response.data)
  }

  /**
   * Given data, creates new entry in database
   *
   * @param playerFormData
   */
  static async create(playerFormData: IPlayerCreateForm): Promise<IServiceResponse<IPlayer>> {
    return this.axios.post('players', playerFormData)
      .then((response: AxiosResponse<IServiceResponse<IPlayer>>) => response.data)
  }

  /**
   * Given data, updates player's entry in database
   *
   * @param data
   */
  static async update(data: IPlayer): Promise<IServiceResponse<IPlayer>> {
    return this.axios.patch(`players/${data.id}`, data)
      .then((response: AxiosResponse<IServiceResponse<IPlayer>>) => response.data)
  }

  static async updateStatus(playerId: number, data: { player_status_id: number }): Promise<IServiceResponse<IPlayer>> {
    return this.axios.patch(`players/${playerId}/update-status`, data)
      .then((response: AxiosResponse<IServiceResponse<IPlayer>>) => response.data)
  }

  static async updatePerformanceProfile(playerId: number, data: { hr_profile: object }): Promise<IServiceResponse<IPlayer>> {
    return this.axios.patch(`players/${playerId}/update-performance-profile`, data)
      .then((response: AxiosResponse<IServiceResponse<IPlayer>>) => response.data)
  }

  /**
   * Given player's id, deletes event in database
   *
   * @param playerId
   */
  static async delete(playerId: string): Promise<AxiosResponse> {
    return this.axios.delete(`players/${playerId}`)
  }

  static async getExternalPlayers(teamId: string | number): Promise<object> {
    return this.axios.get('external/players', {
      params: {
        team_id: teamId
      }
    }).then(response => response.data)
  }

  static async getExternalPolarTeamPlayers(): Promise<object> {
    return this.axios.get('external/polar-team/players').then(response => response.data)
  }

  static async getExternalGpexePlayers(): Promise<object> {
    return this.axios.get('external/gpexe/players').then(response => response.data)
  }

  static async getExternalValdPlayers(): Promise<object> {
    return this.axios.get('external/vald/players').then(response => response.data)
  }

  static async getExternalKinexonPlayers(): Promise<object> {
    return this.axios.get('external/kinexon/players').then(response => response.data)
  }

  static async getExternalStatsportsPlayers(): Promise<object> {
    return this.axios.get('external/statsports/players').then(response => response.data)
  }

  static async getExternalLibrePlayers(): Promise<object> {
    return this.axios.get('external/libre/players').then(response => response.data)
  }

  static async getStats(playerId: number, params: object): Promise<object> {
    return this.axios.get(`players/${playerId}/stats`, {
      params
    }).then(response => response.data)
  }

  static async getDiagnosticStats(playerId: number, params: object): Promise<object> {
    return this.axios.get(`players/${playerId}/diagnostic-stats`, {
      params
    }).then(response => response.data)
  }

  static async getDiagnosticProtocolPlayerStats(playerId: number, diagnosticProtocol: number, params: object): Promise<object> {
    return this.axios.get(`players/${playerId}/diagnostic-protocol/${diagnosticProtocol}`, {
      params
    }).then(response => response.data)
  }

  static async getDiagnosticProtocolPlayerVariables(playerId: number, diagnosticProtocol: number, params: object): Promise<object> {
    return this.axios.get(`players/${playerId}/diagnostic-protocol/${diagnosticProtocol}/variables`, {
      params
    }).then(response => response.data)
  }

  static async getEvents(playerId: number, params: object): Promise<object> {
    return this.axios.get(`players/${playerId}/events`, {
      params
    }).then(response => response.data)
  }

  static async generateEventAttendanceReport(playerId: number, params: object): Promise<object> {
    return this.axios.get(`players/${playerId}/event-attendance-report`, {
      params
    }).then(response => response.data)
  }

  static async getMedia(playerId: number, params: object): Promise<object> {
    return this.axios.get(`players/${playerId}/media`, {
      params
    }).then(response => response.data)
  }

  static async submitNote(playerId: number, data: object): Promise<object> {
    return this.axios.post(`players/${playerId}/note`, data)
      .then(response => response.data)
  }

  static async deleteNote(playerId: number, noteId: number): Promise<AxiosResponse> {
    return this.axios.delete(`players/${playerId}/note/${noteId}`).then(response => response.data)
  }

  static async getNotes(playerId: number, params?: object): Promise<object> {
    return this.axios.get(`players/${playerId}/notes`, { params })
      .then(response => response.data)
  }

  static async getTrainingExercises(playerId: number, params: object): Promise<object> {
    return this.axios.get(`players/${playerId}/training-exercises`, {
      params
    }).then(response => response.data)
  }

  static async getTrainingExerciseHistory(playerId: number, params: object): Promise<object> {
    return this.axios.get(`players/${playerId}/training-exercise-history`, {
      params
    }).then(response => response.data)
  }

  static async getTrainingExerciseStats(playerId: number, params: object): Promise<object> {
    return this.axios.get(`players/${playerId}/training-exercise-stats`, {
      params
    }).then(response => response.data)
  }

  static async getEventAttendanceReport(playerId: string, params = {}): Promise<object> {
    return this.axios.get(`players/${playerId}/event-attendance-report`, {
      params
    }).then(response => response.data)
  }

  static async getMedicalPdfReport(playerId: string, filters: object): Promise<{ url: string }> {
    return this.axios.post(`players/${playerId}/medical/pdf`, { filters })
      .then((response: AxiosResponse<{ url: string }>) => response.data)
  }

  static async getDailyActivityStats(playerId: number, params: object): Promise<object> {
    return this.axios.get(`players/${playerId}/daily-activity-stats`, {
      params
    }).then(response => response.data)
  }

  static async archive(playerId: number): Promise<IServiceResponse<IPlayer>> {
    return this.axios.patch(`players/${playerId}/archive`)
      .then((response: AxiosResponse<IServiceResponse<IPlayer>>) => response.data)
  }

  static async unarchive(playerId: number): Promise<IServiceResponse<IPlayer>> {
    return this.axios.patch(`players/${playerId}/unarchive`)
      .then((response: AxiosResponse<IServiceResponse<IPlayer>>) => response.data)
  }

  static async getDailyStats(params: {
    player_ids?: number[],
    from: string,
    to: string,
    stats_types_variables: object,
    player_status_id?: number,
    player_position_id?: number,
    trend?: boolean,
    aggregation?: boolean,
    latest?: boolean,
    colours?: boolean
  }): Promise<object> {
    return this.axios.get('players/daily-stats', { params }).then(response => response.data)
  }

  static async getDiagnosticsLatest(params: object): Promise<object> {
    return this.axios.get('players/diagnostics-latest', { params }).then(response => response.data)
  }
}
